/**
 * A-06 チェックアウト（お届け先・便・希望日 → 一括確定）＝FR-43・**FR-127（D-83/D-84）**・06 §6.1/§6.2。
 *
 * ★**M1.6＝グループチェックアウト**：カート全体で1回だけ送信し（`POST /partner/checkout`）、
 *   サーバが**卸ごとの子注文**に分ける。**全成功 or 全失敗**（D-84）＝部分成立を作らない。
 *   その代償として「1社の締め超過で全体が不成立」になるので、**どの卸のどの欄が原因か**を
 *   グループの位置で示す（サーバは `field_errors[].field = "groups[i].bin_id"` で返す）。
 * ★**現場はカート全体の既定＋グループごとの上書き**（FR-129）＝毎回同じ現場に届けるのが普通で、
 *   手入力の繰り返しを最初の1回に畳む。上書きを残すのは、卸ごとに納品先が違う場面
 *   （建材は現場、金物は事務所）が実在するため。
 * ★**締め判定はグループ単位**＝締め時刻も便も卸ごとに違う以上、カート全体で1つの判定はできない。
 * ★**氏名の未登録はここで受け止める**（D-87）＝A-09 で必須にしてあるが、画面を閉じた・
 *   古い版のアプリ・API 直叩きのいずれでも「誰の注文か分からない注文」を作らせない。
 *   サーバも 422 `profile_name_required` で拒否する（二重の守り）。
 *
 * ★**「その他」の現場**＝第1区間から続く唯一の要確認フラグ源（06 §6.1 手順4）。
 *   マスタに無い現場を職人が打てるようにし、卸のスタッフが受注詳細で突き合わせる。
 *   ここを塞ぐと「まだ登録していない現場には注文できない」＝現場が使えない。
 */

import {
  ApiError,
  type BuilderProfile,
  type BuilderSite,
  type CheckoutGroupInput,
  type ConnectedTenant,
  type partnerApi,
} from "@kenzai/api-client";
import { Badge, Card, ErrorNote, Money } from "@kenzai/ui";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { GroupTotals, taxOf, useCartGroups, type CartGroup } from "./Cart";
import { fieldStyle, Input } from "./formKit";
import { useCart } from "../cart";
import { useCartUi } from "../cartUi";

type Client = ReturnType<typeof partnerApi>;

/** 冪等キー（05 §1.4）＝**画面を開いた時に1つ作る**。 */
function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/** 1グループぶんの入力。`site` は select の値＝`""`／`b{アドレス帳id}`／`{卸マスタのsite_id}`／`other`。 */
type GroupForm = {
  site: string;
  siteOther: string;
  binId: number | null;
  date: string;
  note: string;
};

const EMPTY_FORM: GroupForm = { site: "", siteOther: "", binId: null, date: "", note: "" };

/** select の値 → API の現場指定（3つの排他）。 */
function siteFields(form: GroupForm): Pick<CheckoutGroupInput, "builder_site_id" | "site_id" | "site_other"> {
  if (form.site.startsWith("b")) return { builder_site_id: Number(form.site.slice(1)) };
  if (form.site === "other") return { site_other: form.siteOther.trim() };
  if (form.site !== "") return { site_id: Number(form.site) };
  return {};
}

export function CheckoutSheetBody({ client, connections }: { client: Client; connections: ConnectedTenant[] }) {
  const cart = useCart();
  const ui = useCartUi();
  const { groups, payable, grandTotal } = useCartGroups(client, connections);

  // 現場アドレス帳（FR-129・D-86）＝**工務店グループで共有**。卸に依らない自分たちの現場。
  const book = useQuery({ queryKey: ["builder-sites"], queryFn: () => client.builderSites() });

  const siteQueries = useQueries({
    queries: payable.map((g) => ({
      queryKey: ["sites", g.tenant.tenant_id],
      queryFn: () => client.sites(g.tenant.tenant_id),
    })),
  });
  // FR-18・D-63：現場「その他」の初期値（いつものお届け先）と、**氏名の未登録判定**（D-87）
  const profileQueries = useQueries({
    queries: payable.map((g) => ({
      queryKey: ["profile", g.tenant.tenant_id],
      queryFn: () => client.profile(g.tenant.tenant_id),
    })),
  });

  const [forms, setForms] = useState<Record<number, GroupForm>>({});
  const [personName, setPersonName] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  /** サーバが返したグループ位置つきのエラー（tenant_id → 文言）。 */
  const [serverErrors, setServerErrors] = useState<Map<number, string>>(new Map());
  /** 送信を1回押すまでは必須の赤字を出さない（開いた瞬間に赤い画面にしない）。 */
  const [showLocal, setShowLocal] = useState(false);

  // ★**キーはこの画面の生存中ずっと同じ**＝送信ボタンを2回押しても2件にならない。
  //   再送のたびに作り直すと冪等キーの意味が無い（05 §1.4）。失敗した送信は1件も作らない
  //   （サーバ側が1トランザクション＝D-84）ので、直して送り直しても 409 にはならない。
  const [idemKey] = useState(newIdempotencyKey);

  const setForm = (tenantId: number, patch: Partial<GroupForm>) =>
    setForms((prev) => ({ ...prev, [tenantId]: { ...(prev[tenantId] ?? EMPTY_FORM), ...patch } }));

  // 便と希望日の既定（06 §6.2：**締め後は翌営業日を既定候補にする**＝「選べません」で止めない）。
  // グループごとに hook を置けないので、まとめて1つの effect で埋める。
  const binsSignal = payable.map((g) => `${g.tenant.tenant_id}:${g.bins?.length ?? -1}`).join();
  useEffect(() => {
    setForms((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const g of payable) {
        const bins = g.bins;
        if (!bins?.length) continue;
        const cur = next[g.tenant.tenant_id] ?? EMPTY_FORM;
        if (cur.binId !== null) continue;
        const first = bins.find((b) => b.available_today) ?? bins[0];
        next[g.tenant.tenant_id] = { ...cur, binId: first.bin_id, date: first.earliest_date };
        changed = true;
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- payable は毎回新しい配列＝中身の signal で見る
  }, [binsSignal]);

  if (groups.length === 0) {
    return (
      <div>
        <div className="sh-title">発注内容の確認</div>
        <p className="oa-lead">カートは空です。</p>
      </div>
    );
  }

  const profiles = payable.map((_g, i) => profileQueries[i]?.data);
  // 氏名が未登録の接続（D-87）＝**1つでもあれば**送れない（サーバも同じ判定をする）。
  // ★**読み込み中を「未登録」と数えない**＝数えると、開いた直後に氏名欄が一瞬出て消える
  //   （何を聞かれたのか分からないまま画面が動く）。取りこぼしはサーバの 422 が受け止める。
  const missingName = payable.filter((_g, i) => profiles[i] !== undefined && profiles[i]!.person_name === null);
  const needName = missingName.length > 0;

  /** クライアント側の必須チェック（サーバの 422 と同じ場所を指す）。 */
  function localErrors(): Map<number, string> {
    const errs = new Map<number, string>();
    for (const g of payable) {
      const f = forms[g.tenant.tenant_id] ?? EMPTY_FORM;
      if (f.site === "") errs.set(g.tenant.tenant_id, "お届け先をお選びください。");
      else if (f.site === "other" && f.siteOther.trim() === "")
        errs.set(g.tenant.tenant_id, "現場名・お届け先をご入力ください。");
      else if (f.binId === null) errs.set(g.tenant.tenant_id, "配送便をお選びください。");
      else if (f.date === "") errs.set(g.tenant.tenant_id, "お届け希望日をご入力ください。");
    }
    return errs;
  }

  const local = localErrors();
  const groupError = (tenantId: number): string | undefined =>
    serverErrors.get(tenantId) ?? (showLocal ? local.get(tenantId) : undefined);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setShowLocal(true);
    setServerErrors(new Map());
    setError(null);
    if (needName && personName.trim() === "") return;
    if (local.size > 0) return;

    setBusy(true);
    const sent = payable.map((g) => g.tenant.tenant_id);
    try {
      // ① 氏名の登録（D-87・D-63＝**接続ごと**に持つので、未登録の接続すべてに入れる）
      for (let i = 0; i < payable.length; i += 1) {
        const p: BuilderProfile | undefined = profiles[i];
        if (!p || p.person_name !== null) continue;
        await client.updateProfile(payable[i].tenant.tenant_id, {
          person_name: personName.trim(),
          phone: p.phone,
          address: p.address,
          updated_at: p.updated_at,
        });
      }

      // ② 一括確定（全成功 or 全失敗＝D-84）
      const body: CheckoutGroupInput[] = payable.map((g) => {
        const f = forms[g.tenant.tenant_id] ?? EMPTY_FORM;
        return {
          tenant_id: g.tenant.tenant_id,
          items: g.lines.map((l) => ({ product_id: l.product_id, qty: String(l.qty) })),
          desired_date: f.date,
          bin_id: f.binId as number,
          note: f.note.trim() === "" ? null : f.note.trim(),
          ...siteFields(f),
        };
      });
      const res = await client.checkout(body, idemKey);

      // ★完了シートに出す「送付先・お届け」＝**確定時のフォーム値の控え**（親の API は
      //   現場・希望日を持たない＝D-85）。卸ごとに値が割れたときは null（1つを言い切らない）。
      const labels = payable.map((g, i) => {
        const f = forms[g.tenant.tenant_id] ?? EMPTY_FORM;
        if (f.site.startsWith("b")) {
          return (book.data ?? []).find((s2) => s2.id === Number(f.site.slice(1)))?.name ?? null;
        }
        if (f.site === "other") return f.siteOther.trim() || null;
        return (siteQueries[i]?.data ?? []).find((s2) => String(s2.site_id) === f.site)?.name ?? null;
      });
      const dates = payable.map((g) => (forms[g.tenant.tenant_id] ?? EMPTY_FORM).date);
      const uniq = (xs: (string | null)[]): string | null =>
        xs.length > 0 && xs.every((x) => x !== null && x === xs[0]) ? xs[0] : null;

      // ★**シートを完了に切り替えてから**カートを空にする（先に空にすると、この画面が
      //   「カートは空です」に描き替わってから切り替わることになる＝旧 /done 遷移と同じ罠）。
      ui.showDone(res, { shipLabel: uniq(labels), date: uniq(dates) });
      // 送れた卸のぶんだけ空にする＝停止中でカートに残した品を消さない
      cart.clearMany(sent);
    } catch (err) {
      if (err instanceof ApiError) setServerErrors(mapGroupErrors(err, sent));
      setError(err);
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="sh-title">発注内容の確認</div>
      <form onSubmit={submit} noValidate data-testid="checkout-form" style={{ display: "grid", gap: "var(--kz-space-4)" }}>
        <ErrorNote error={error} />

        {/* ★中身の編集は A-10 カート（D-80）＝ここは**どこへ・いつ届けるかを決める画面**。
            合計だけ再掲して「何を送ろうとしているか」を見失わないようにする。 */}
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>
              <span className="kz-num">{payable.length}</span> 社 ／{" "}
              <span className="kz-num">{payable.reduce((n, g) => n + g.lines.length, 0)}</span> 品目
            </span>
            <span style={{ fontWeight: 700, fontSize: "var(--kz-font-size-lg)" }}>
              <Money value={grandTotal === null ? null : String(grandTotal)} />
              <span
                style={{ fontSize: "var(--kz-font-size-sm)", fontWeight: 400, color: "var(--kz-color-ink-muted)" }}
              >
                （税抜）
              </span>
            </span>
          </div>
          <button
            type="button"
            data-testid="back-to-cart"
            onClick={() => ui.openCart()}
            className="kz-pressable"
            style={{
              marginTop: "var(--kz-space-3)",
              minHeight: 44,
              width: "100%",
              border: "1px solid var(--kz-color-border)",
              borderRadius: "var(--kz-radius-sm)",
              background: "var(--kz-color-surface)",
              font: "inherit",
              cursor: "pointer",
            }}
          >
            内容を見直す
          </button>
        </Card>

        {needName && (
          <Card>
            <h2 style={{ margin: "0 0 var(--kz-space-2)", fontSize: "var(--kz-font-size-md)" }}>お名前</h2>
            <p
              style={{
                margin: "0 0 var(--kz-space-3)",
                fontSize: "var(--kz-font-size-sm)",
                color: "var(--kz-color-ink-muted)",
              }}
            >
              {/* なぜ要るかを書く＝LINE の表示名は本名とは限らず、卸は誰からの注文か分からない */}
              ご注文の前に、お名前のご登録をお願いします（
              {missingName.map((g) => g.tenant.tenant_name).join("・")} に伝わります）。
            </p>
            <Input
              label="お名前（必須）"
              value={personName}
              onChange={setPersonName}
              maxLength={100}
              required
              data-testid="checkout-name"
            />
            {showLocal && personName.trim() === "" && (
              <p style={{ margin: 0, color: "var(--kz-status-danger)", fontSize: "var(--kz-font-size-sm)" }}>
                お名前をご入力ください。
              </p>
            )}
          </Card>
        )}

        {/* カート全体の既定（FR-129）＝**押した瞬間に全グループへ入る**（後から個別に直せる）。
            自動で追従させないのは、1社だけ現場を変えた後にここを触ると、その上書きが
            黙って戻ってしまうため。 */}
        {(book.data?.length ?? 0) > 0 && payable.length > 1 && (
          <Card>
            <Select
              label="お届け先（すべてのお取引先に反映）"
              value=""
              data-testid="default-site-select"
              onChange={(v) => {
                if (v === "") return;
                for (const g of payable) setForm(g.tenant.tenant_id, { site: v });
              }}
              options={[
                { value: "", label: "現場を選ぶ…" },
                ...(book.data ?? [])
                  .filter((s) => s.status === "active")
                  .map((s) => ({ value: `b${s.id}`, label: s.name })),
              ]}
            />
          </Card>
        )}

        {groups.map((g) => {
          const idx = payable.findIndex((p) => p.tenant.tenant_id === g.tenant.tenant_id);
          return (
            <GroupCard
              key={g.tenant.tenant_id}
              group={g}
              form={forms[g.tenant.tenant_id] ?? EMPTY_FORM}
              onChange={(patch) => setForm(g.tenant.tenant_id, patch)}
              book={book.data ?? []}
              masterSites={idx >= 0 ? (siteQueries[idx]?.data ?? []) : []}
              defaultAddress={idx >= 0 ? (profiles[idx]?.address ?? null) : null}
              error={groupError(g.tenant.tenant_id)}
              showVendorName={connections.length > 1}
            />
          );
        })}

        {/* 全体合計（D-101・税は表示のみ）＝確定の直前にもう一度、同じ計算で言う */}
        <div className="ck-totals" style={{ marginTop: 0 }}>
          <div className="ct-line">
            <span>小計（税抜）</span>
            <span className="kz-num">
              <Money value={grandTotal === null ? null : String(grandTotal)} />
            </span>
          </div>
          <div className="ct-line">
            <span>消費税（10%）</span>
            <span className="kz-num">
              <Money value={grandTotal === null ? null : String(taxOf(grandTotal))} />
            </span>
          </div>
          <div className="ct-total">
            <span>合計（税込）</span>
            <span className="kz-num">
              <Money value={grandTotal === null ? null : String(grandTotal + taxOf(grandTotal))} />
            </span>
          </div>
        </div>

        {/* この画面の最終アクション＝黄 CTA（D-98④・LP／デモの「押す場所だけ黄」） */}
        <button
          type="submit"
          disabled={busy || payable.length === 0}
          data-testid="submit-order"
          className="kz-btn kz-btn--accent kz-pressable"
          style={{
            minHeight: 56,
            fontSize: "var(--kz-font-size-lg)",
            fontWeight: 700,
            cursor: busy ? "wait" : "pointer",
          }}
        >
          {busy ? "送信中…" : payable.length > 1 ? `${payable.length}社にまとめて注文する` : "この内容で注文する"}
        </button>
        {/* 確定の直前に「何が起きるか」を言う（Design の文言。請求の主体は変わらない＝LC 系の約束） */}
        <p
          style={{
            margin: 0,
            fontSize: "var(--kz-font-size-sm)",
            color: "var(--kz-color-ink-muted)",
            textAlign: "center",
          }}
        >
          この操作で各お取引先（卸売業者）に注文が送信されます。請求書はこれまでどおり各卸から届きます。
        </p>
      </form>
    </div>
  );
}

/**
 * サーバの 422／400 をグループ（卸）に割り当てる。
 *
 * ★サーバは `groups[i].bin_id` の形で**送信した並びの位置**を返す（checkout_service が
 *   ロック順のために並べ替えても、位置は利用者が送った順のまま返る）。だから
 *   `sent[i]` で卸に戻せる。位置が無い（全体の）エラーは Map に入れず、上の ErrorNote に出す。
 * ★`extra.group_index` も返っているが、共有の `ApiError` は `field_errors` までしか
 *   持たない（api-client の責務を増やさない）＝位置は field 名から取る。
 */
function mapGroupErrors(err: ApiError, sent: number[]): Map<number, string> {
  const out = new Map<number, string>();
  for (const fe of err.fieldErrors) {
    const m = /^groups\[(\d+)\]/.exec(fe.field);
    if (!m) continue;
    const tenantId = sent[Number(m[1])];
    if (tenantId === undefined) continue;
    // 同じグループに複数の指摘が来たら、最初のものを出す（欄ごとの表示は M2）
    if (!out.has(tenantId)) out.set(tenantId, fe.message);
  }
  return out;
}

function GroupCard({
  group,
  form,
  onChange,
  book,
  masterSites,
  defaultAddress,
  error,
  showVendorName,
}: {
  group: CartGroup;
  form: GroupForm;
  onChange: (patch: Partial<GroupForm>) => void;
  book: BuilderSite[];
  masterSites: { site_id: number; name: string }[];
  defaultAddress: string | null;
  error?: string;
  showVendorName: boolean;
}) {
  const suspended = group.tenant.tenant_status === "suspended";

  // 現場「その他」を選んだら、まだ何も打っていないときだけプロフィールの住所を入れる
  // （**打ちかけの内容を上書きしない**＝入力中に消えるのが一番困る）。
  useEffect(() => {
    if (form.site !== "other" || form.siteOther !== "") return;
    if (defaultAddress) onChange({ siteOther: defaultAddress });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onChange は毎回新しい関数
  }, [form.site, form.siteOther, defaultAddress]);

  // 選択中のアドレス帳の現場（M1.9・D-98）＝住所・担当・メモをカードで見せる。
  const selectedBook = form.site.startsWith("b")
    ? (book.find((s) => s.id === Number(form.site.slice(1))) ?? null)
    : null;

  // ★現場のメモ（置き場所など）を**備考へプレフィル**＝これが卸に伝わる唯一の経路
  //   （builder_sites.note は職人側の控えで、注文に自動では載らない＝D-86 のプレフィル方式）。
  //   「その他」の住所と同じ規律＝**まだ何も打っていないときだけ**入れる（打ちかけを消さない）。
  useEffect(() => {
    if (!selectedBook?.note || form.note !== "") return;
    onChange({ note: selectedBook.note });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onChange は毎回新しい関数
  }, [form.site]);

  if (suspended) {
    return (
      <Card data-testid={`checkout-group-${group.tenant.tenant_id}`}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--kz-space-2)" }}>
          <h2 style={{ margin: 0, fontSize: "var(--kz-font-size-md)" }}>{group.tenant.tenant_name}</h2>
          <Badge tone="danger">利用できません</Badge>
        </div>
        <p style={{ margin: "var(--kz-space-2) 0 0", fontSize: "var(--kz-font-size-sm)" }}>
          この卸へのご注文は、いまお送りできません。この分はカートに残りますので、
          お取引先にご確認のうえ改めてご注文ください。
        </p>
      </Card>
    );
  }

  const selectedBin = group.bins?.find((b) => b.bin_id === form.binId) ?? null;

  return (
    <Card data-testid={`checkout-group-${group.tenant.tenant_id}`}>
      {showVendorName && (
        <h2 style={{ margin: "0 0 var(--kz-space-3)", fontSize: "var(--kz-font-size-md)" }}>
          {group.tenant.tenant_name}
        </h2>
      )}
      {error && (
        <p
          role="alert"
          data-testid={`group-error-${group.tenant.tenant_id}`}
          style={{
            margin: "0 0 var(--kz-space-3)",
            background: "var(--kz-status-danger-weak)",
            color: "var(--kz-status-danger)",
            border: "1px solid currentColor",
            borderRadius: "var(--kz-radius-sm)",
            padding: "var(--kz-space-3)",
            fontSize: "var(--kz-font-size-sm)",
          }}
        >
          {error}
        </p>
      )}

      <Select
        label="お届け先"
        value={form.site}
        onChange={(v) => onChange({ site: v })}
        required
        data-testid="site-select"
        options={[
          { value: "", label: "選んでください" },
          // アドレス帳（FR-129）＝**卸のマスタに無くても選べる**（名前だけ渡す＝プレフィル方式）
          ...book
            .filter((s) => s.status === "active")
            .map((s) => ({ value: `b${s.id}`, label: s.name })),
          ...masterSites.map((s) => ({ value: String(s.site_id), label: s.name })),
          // ★ 06 §6.1 手順4：**マスタに無い現場のための逃げ道**（＝要確認フラグ源）
          { value: "other", label: "その他（現場名を入力）" },
        ]}
      />
      {form.site === "other" && (
        <Input
          label="現場名・お届け先"
          value={form.siteOther}
          onChange={(v) => onChange({ siteOther: v })}
          required
          data-testid="site-other"
          hint={
            defaultAddress
              ? "いつものお届け先を入れてあります。違う場合は書き換えてください。"
              : "担当者が確認のうえ登録します。"
          }
        />
      )}
      {/* 選んだ現場の中身（M1.9・Design の ship-card）＝「どこの・誰宛か」を選んだその場で確かめる */}
      {selectedBook && (
        <div
          data-testid={`site-info-${group.tenant.tenant_id}`}
          style={{
            margin: "0 0 var(--kz-space-3)",
            padding: "var(--kz-space-3)",
            background: "var(--kz-color-primary-weak)",
            borderRadius: "var(--kz-radius-sm)",
            fontSize: "var(--kz-font-size-sm)",
            display: "grid",
            gap: 2,
          }}
        >
          {selectedBook.address && <span>{selectedBook.address}</span>}
          {(selectedBook.contact_person || selectedBook.phone) && (
            <span style={{ color: "var(--kz-color-ink-muted)" }}>
              {selectedBook.contact_person}
              {selectedBook.contact_person && selectedBook.phone && "・"}
              {selectedBook.phone}
            </span>
          )}
          {selectedBook.note && (
            <span style={{ color: "var(--kz-status-warn)" }}>{selectedBook.note}（備考に入れてあります）</span>
          )}
        </div>
      )}

      <Select
        label="配送便"
        value={String(form.binId ?? "")}
        onChange={(v) => {
          const b = group.bins?.find((x) => x.bin_id === Number(v));
          onChange({
            binId: Number(v),
            ...(b && form.date < b.earliest_date ? { date: b.earliest_date } : {}),
          });
        }}
        data-testid="bin-select"
        options={(group.bins ?? []).map((b) => ({
          value: String(b.bin_id),
          // 締めは「便の受付締め」＝注文自体は24時間受け付ける（06 §6.2）
          label: b.available_today
            ? `${b.name}（本日 ${b.cutoff_time} まで受付）`
            : `${b.name}（本日の受付は終了・${b.cutoff_time} 締め）`,
        }))}
      />
      <Input
        label="お届け希望日"
        type="date"
        value={form.date}
        min={selectedBin?.earliest_date}
        onChange={(v) => onChange({ date: v })}
        required
        data-testid="date-input"
        hint={
          selectedBin && !selectedBin.available_today
            ? "本日の受付は終了しています。翌営業日以降をお選びください。"
            : undefined
        }
      />
      <Input
        label="備考（任意）"
        value={form.note}
        onChange={(v) => onChange({ note: v })}
        data-testid="note-input"
      />

      {/* 明細の再掲（Design ck-sup）＝「何を・いくつ・いくらで」を確定の直前にもう一度 */}
      <div className="ck-items" style={{ marginBottom: "var(--kz-space-2)" }}>
        {group.lines.map((l) => (
          <div key={l.product_id} className="ck-row" data-testid={`checkout-line-${l.product_id}`}>
            <span>
              <b>{l.name}</b>
              <small className="kz-num">
                {l.qty}
                {l.unit}
                {l.unit_price !== null && ` × ¥${Number(l.unit_price).toLocaleString("ja-JP")}`}
              </small>
            </span>
            <span className="ck-amt kz-num">
              <Money value={l.unit_price === null ? null : String(Number(l.unit_price) * l.qty)} />
            </span>
          </div>
        ))}
      </div>

      {/* 確定の直前でも**カートと同じ内訳**を出す（D-90）＝ここで消えると
          「カートの合計と請求額が違う」という問い合わせの元になる。 */}
      <GroupTotals group={group} />
    </Card>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
  ...rest
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
} & Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "onChange" | "value">) {
  return (
    <label style={{ display: "block", marginBottom: "var(--kz-space-3)" }}>
      <span style={{ display: "block", fontSize: "var(--kz-font-size-sm)", fontWeight: 600, marginBottom: 4 }}>
        {label}
      </span>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={fieldStyle} {...rest}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
